import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    AreaChart,
    Area,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts';
import './dashboardTest.css';

import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleIcon from '@mui/icons-material/People';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CircularProgress from '@mui/material/CircularProgress';
import { getAllSales, getAllSalesByAgentId, GetTotalFinalPriceByGroupLast30Days } from '../../api/salesApi';
import { useUser } from '../../context/userContext';
import { getCommissionProgressByAgentId } from '../../api/commissionProgressApi';

// Import your API (adjust path as needed)
// import { api } from '../services/api';

export const DashboardTest = ({ currentRole }) => {
    const { user } = useUser();
    const userId = user?.userId;
    const [loading, setLoading] = useState(true);
    const [sales, setSales] = useState([]);
    const [commissions, setCommissions] = useState([]);

    // Chart States
    const [dailyTrendData, setDailyTrendData] = useState([]);
    const [teamGroups, setTeamGroups] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // TODO: Replace with your actual API calls
                // const [salesData, commissionsData] = await Promise.all([
                //     api.sales.getAll(),
                //     api.commissions.getAll()
                // ]);

                // Mock data for demonstration
                const salesData = await getAllSalesByAgentId(userId);
                const commissionsData = await getCommissionProgressByAgentId(userId);
                const groupSalesData = await GetTotalFinalPriceByGroupLast30Days();

                setSales(salesData.data);
                setCommissions(commissionsData.data);

                // Process Data for Charts (Last 30 Days)
                const dateMap = new Map();
                const today = new Date();
                const groups = new Set();

                for (let i = 29; i >= 0; i--) {
                    const d = new Date();
                    d.setDate(today.getDate() - i);
                    const dateStr = d.toISOString().split('T')[0];
                    const displayStr = `${d.getDate()}/${d.getMonth() + 1}`;

                    dateMap.set(dateStr, {
                        date: dateStr,
                        displayDate: displayStr,
                        totalRevenue: 0
                    });
                }

                // Populate Data from Sales
                salesData.data.forEach(sale => {
                    const saleDate = sale.createdOn.split('T')[0];

                    if (dateMap.has(saleDate)) {
                        const entry = dateMap.get(saleDate);
                        entry.totalRevenue += sale.finalPrice;
                    }
                });

                // Populate Data from GroupSales
                groupSalesData.data.forEach(groupSale => {
                    const groupSaleDate = groupSale.createdOn.split('T')[0];

                    const groupName = groupSale.groupName;
                    groups.add(groupName);

                    if (dateMap.has(groupSaleDate)) {
                        const entry = dateMap.get(groupSaleDate);
                        entry[groupName] = groupSale.totalFinalPrice;
                    }
                });

                const processedData = Array.from(dateMap.values());
                setDailyTrendData(processedData);
                setTeamGroups(Array.from(groups));

            } catch (error) {
                console.error("Failed to fetch dashboard data", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Calculate metrics
    const totalRevenue = sales.reduce((acc, sale) => acc + sale.finalPrice, 0);
    const paidCommission = commissions.filter(c => c.commissionProgressDto.paymentStatus === 'Complete').reduce((acc, comm) => acc + comm.commissionProgressDto.commPay, 0);
    const pendingCommission = commissions.filter(c => c.commissionProgressDto.paymentStatus === 'In Progress' || !c.commissionProgressDto.paymentStatus).reduce((acc, c) => acc + c.commissionProgressDto.commPay, 0);
    const pendingClosingSales = sales.filter(sale => sale.status !== 'Complete').length;

    // Check if user can view all data (adjust based on your UserRole enum)
    const canViewAll = ['Super Admin', 'Admin', 'Accountant'].includes(currentRole);

    // Colors for Team Chart
    const TEAM_COLORS = ['#3b82f6', '#ec4899', '#8b5cf6', '#10b981', '#f59e0b', '#6366f1'];

    if (loading) {
        return (
            <div className="dashboard-loading">
                <CircularProgress size={40} />
                <span className="loading-text">Loading Dashboard data...</span>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <h1 className="dashboard-title">
                    {canViewAll ? 'Company Overview' : 'My Performance'}
                </h1>
                <div className="dashboard-date">
                    <CalendarTodayIcon fontSize="small" />
                    <span>Last 30 Days</span>
                </div>
            </div>

            {/* Metrics Cards */}
            <div className="metrics-grid">
                <div className="metric-card">
                    <div className="metric-content">
                        <div className="metric-info">
                            <p className="metric-label">Total Revenue</p>
                            <h3 className="metric-value">
                                RM {totalRevenue.toLocaleString()}
                            </h3>
                        </div>
                        <div className="metric-icon metric-icon-green">
                            <AttachMoneyIcon />
                        </div>
                    </div>
                    {/* <div className="metric-trend metric-trend-up">
                        <TrendingUpIcon fontSize="small" />
                        <span className="trend-value">12.5%</span>
                        <span className="trend-label">vs last month</span>
                    </div> */}
                </div>

                <div className="metric-card">
                    <div className="metric-content">
                        <div className="metric-info">
                            <p className="metric-label">Commissions Paid</p>
                            <h3 className="metric-value">
                                RM {paidCommission.toLocaleString()}
                            </h3>
                        </div>
                        <div className="metric-icon metric-icon-blue">
                            <ShowChartIcon />
                        </div>
                    </div>
                    {/* <div className="metric-footer">
                        <span>Processed yesterday</span>
                    </div> */}
                </div>

                <div className="metric-card">
                    <div className="metric-content">
                        <div className="metric-info">
                            <p className="metric-label">Pending Commission</p>
                            <h3 className="metric-value">
                                RM {pendingCommission.toLocaleString()}
                            </h3>
                        </div>
                        <div className="metric-icon metric-icon-amber">
                            <ShowChartIcon />
                        </div>
                    </div>
                    <div className="metric-footer metric-footer-warning">
                        <span>Wait for approval</span>
                    </div>
                </div>

                <div className="metric-card">
                    <div className="metric-content">
                        <div className="metric-info">
                            <p className="metric-label">Active Sales</p>
                            <h3 className="metric-value">{sales.length}</h3>
                        </div>
                        <div className="metric-icon metric-icon-indigo">
                            <PeopleIcon />
                        </div>
                    </div>
                    <div className="metric-footer metric-footer-info">
                        <span>{pendingClosingSales} Pending Closure</span>
                    </div>
                </div>
            </div>

            {/* Charts Section */}
            <div className="charts-grid">
                {/* Chart 1: Total Revenue Trend */}
                <div className="chart-card">
                    <h3 className="chart-title">Total Revenue Trend (30 Days)</h3>
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={dailyTrendData} margin={{ left: 10, right: 10 }}>
                                <defs>
                                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis
                                    dataKey="displayDate"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#64748b', fontSize: 12 }}
                                    interval={4}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#64748b', fontSize: 12 }}
                                    tickFormatter={(val) => `${(val / 1000).toFixed(0)}k`}
                                />
                                <Tooltip
                                    cursor={{ stroke: '#10b981', strokeWidth: 1 }}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                    formatter={(value) => [`RM ${value.toLocaleString()}`, 'Revenue']}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="totalRevenue"
                                    stroke="#10b981"
                                    fillOpacity={1}
                                    fill="url(#colorRevenue)"
                                    strokeWidth={2}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Chart 2: Team Performance */}
                <div className="chart-card">
                    <h3 className="chart-title">Team Performance (Daily Revenue)</h3>
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={dailyTrendData} margin={{ left: 10, right: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis
                                    dataKey="displayDate"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#64748b', fontSize: 12 }}
                                    interval={4}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#64748b', fontSize: 12 }}
                                    tickFormatter={(val) => `${(val / 1000).toFixed(0)}k`}
                                />
                                <Tooltip
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                    formatter={(value, name) => [`RM ${value.toLocaleString()}`, name]}
                                />
                                <Legend iconType="circle" wrapperStyle={{ paddingTop: '10px' }} />

                                {teamGroups.map((group, index) => (
                                    <Line
                                        key={group}
                                        type="monotone"
                                        dataKey={group}
                                        name={group}
                                        stroke={TEAM_COLORS[index % TEAM_COLORS.length]}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={{ r: 6 }}
                                        connectNulls
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Recent Sales Table */}
            <div className="table-card">
                <div className="table-header">
                    <h3 className="table-title">Recent Sales Update</h3>
                    <Link to="/sales" className="table-link">
                        View All Sales
                    </Link>
                </div>
                <div className="table-wrapper">
                    <table className="sales-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Customer</th>
                                <th>Product Energy (kWp)</th>
                                <th>Product Category</th>
                                {/* <th>Group</th> */}
                                <th>Price</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sales.slice(0, 4).map((sale, index) => (
                                <tr key={sale.id || `sale-${index}`}>
                                    <td className="table-id">{sale.salesId}</td>
                                    <td>{sale.customerDetails.customerName}</td>
                                    <td>{sale.energy}</td>
                                    <td>{sale.productCategory}</td>
                                    {/* <td className="table-group">{sale.agentGroup}</td> */}
                                    <td className="table-price">RM {sale.finalPrice.toLocaleString()}</td>
                                    <td>
                                        {/* <span className={`status-badge status-${(sale.status ?? 'Unknown').toLowerCase().replace(/\s+/g, '-')}`}> */}
                                        <span className={`status-badge status-${(sale.status ?? 'Unknown')}`}>
                                            {(sale.status ?? 'Unknown')}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
